import { equals } from 'ramda'
import { useCallback, useEffect, useMemo } from 'react'

import { usePrevious } from '@/app/helpers'

export type SearchableQuery = {
  page: number
  searchTerm: string
}

export type SearchableProps<Query extends SearchableQuery> = {
  orgId: number
  pageSearchTerm: string
  query: Query

  getData: (query: Query) => Promise<void>
  navigate: (query: Query) => void
  reset: () => void
}

export const useSearchable = <Query extends SearchableQuery>(props: SearchableProps<Query>) => {
  const { orgId, pageSearchTerm, query, getData, navigate, reset } = props
  const previousQuery = usePrevious(query)

  // biome-ignore lint/correctness/useExhaustiveDependencies(orgId): if orgId changes, reset the search
  useEffect(() => {
    reset()
  }, [orgId, reset]) // TODO: remove this effect and handle orgId changes in the parent component

  useEffect(() => {
    // if query not changed, do nothing
    if (equals(query, previousQuery)) {
      return
    }

    getData(query)
  }, [previousQuery, query, getData])

  const onSubmit = useCallback(
    (searchTerm: string) =>
      navigate({
        ...query,
        page: 1,
        searchTerm,
      }),
    [query, navigate],
  )

  return useMemo(
    () => ({
      searchTerm: query.searchTerm || pageSearchTerm,
      onSubmit,
    }),
    [pageSearchTerm, query.searchTerm, onSubmit],
  )
}
